import React, { useMemo, useState } from 'react';

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";

function Avatar({ team, size = 28 }) {
  const initial = (team?.displayName || '?').trim().charAt(0).toUpperCase();
  if (team?.avatarUrl) {
    return (
      <img
        src={team.avatarUrl}
        alt=""
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#E5E5EA' }}
        onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
      />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: '#E5E5EA', color: '#6E6E73', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700 }}>
      {initial}
    </div>
  );
}

/**
 * Projected draft board for the next rookie draft (earliest future season only —
 * anything further out changes too much to project).
 *
 * Columns are draft slots, ordered by projected finish: non-playoff teams first
 * (worst Max Points For = pick 1.01), then playoff teams in reverse finish
 * (champion picks last). Each column header is the team that EARNED that slot.
 * Each cell is that slot's pick in a given round — and if the pick was traded,
 * the cell shows who actually owns it now.
 */
export default function DraftOrder({ bootstrap, results, myRosterId, tradeAdjustments, onSelectTeam }) {
  const [hoverSlot, setHoverSlot] = useState(null); // {x, y, pick, dist} — who lands in this slot
  const playoffTeams = bootstrap.playoffTeams || 6;
  const teams = bootstrap.teams;

  // Earliest tradeable future season = "next year's draft". Only show this one.
  const season = useMemo(() => {
    const s = new Set();
    teams.forEach(t => (t.picks || []).forEach(p => s.add(p.season)));
    return [...s].sort()[0] || '2027';
  }, [teams]);

  const maxRound = useMemo(() => {
    let m = 1;
    teams.forEach(t => (t.picks || []).forEach(p => { if (p.season === season) m = Math.max(m, p.round); }));
    return m;
  }, [teams, season]);

  // ── Projected draft slot order (which team's native pick sits at each slot) ──
  const slotOrder = useMemo(() => {
    const withResults = teams.map(t => ({ team: t, r: results.teams[t.rosterId] })).filter(x => x.r);
    const byPlayoff = [...withResults].sort((a, b) => b.r.playoffProbability - a.r.playoffProbability);
    const playoffSet = new Set(byPlayoff.slice(0, playoffTeams).map(x => x.team.rosterId));

    const nonPlayoff = withResults.filter(x => !playoffSet.has(x.team.rosterId))
      .sort((a, b) => (a.r.maxPointsFor?.p50 || 0) - (b.r.maxPointsFor?.p50 || 0));
    const playoff = withResults.filter(x => playoffSet.has(x.team.rosterId))
      .sort((a, b) => (a.r.championshipProbability || 0) - (b.r.championshipProbability || 0));

    return [...nonPlayoff, ...playoff].map(x => ({ rosterId: x.team.rosterId, madePlayoffs: playoffSet.has(x.team.rosterId) }));
  }, [teams, results, playoffTeams]);

  // Picks moved by the active trade — overlay them so the board reflects the deal.
  // acquiredKeys = picks the trade sends to ME; sentKeys = picks I send away.
  const { ownerOfPick, acquiredKeys, sentKeys } = useMemo(() => {
    const m = {};
    teams.forEach(t => (t.picks || []).forEach(p => {
      m[`${p.season}:${p.round}:${p.originalRosterId}`] = t.rosterId;
    }));
    const acquired = new Set();
    const sent = new Set();
    (tradeAdjustments || []).forEach(adj => {
      [...(adj.picksGiven || []), ...(adj.picksReceived || [])].forEach(pk => {
        if (pk.season == null || pk.round == null || pk.originalRosterId == null) return;
        const key = `${pk.season}:${pk.round}:${pk.originalRosterId}`;
        if (pk.toRosterId != null) m[key] = pk.toRosterId;
        if (pk.toRosterId === myRosterId) acquired.add(key);
        else if (pk.fromRosterId === myRosterId) sent.add(key);
      });
    });
    return { ownerOfPick: m, acquiredKeys: acquired, sentKeys: sent };
  }, [teams, tradeAdjustments, myRosterId]);

  const teamById = useMemo(() => {
    const m = {};
    teams.forEach(t => { m[t.rosterId] = t; });
    return m;
  }, [teams]);

  const COL_W = 112;
  const rounds = Array.from({ length: maxRound }, (_, i) => i + 1);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', fontFamily: ff }}>
      {/* Header */}
      <div style={{ flexShrink: 0, padding: '20px 24px 14px' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.02em' }}>Projected {season} draft</div>
        <div style={{ fontSize: 13, color: '#86868B', marginTop: 2 }}>
          Worst Max&nbsp;PF picks 1.01 · hover a slot to see who's likely to land there · traded picks show their new owner
        </div>
      </div>

      {/* Board — horizontal scroll, columns = slots, rows = rounds */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 24px' }}>
        <div style={{ display: 'flex', gap: 6, minWidth: 'min-content' }}>
          {slotOrder.map((slot, i) => {
            const slotNum = i + 1;
            const originalTeam = teamById[slot.rosterId];
            return (
              <div key={slot.rosterId} style={{ width: COL_W, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* Column header — the team that earned this slot */}
                <div
                  onClick={() => onSelectTeam && onSelectTeam(slot.rosterId)}
                  onMouseEnter={e => {
                    const r = e.currentTarget.getBoundingClientRect();
                    const dist = teams
                      .map(t => ({ team: t, p: results.teams[t.rosterId]?.draftSlotProbs?.[i] || 0 }))
                      .filter(x => x.p > 0.005)
                      .sort((a, b) => b.p - a.p)
                      .slice(0, 6);
                    setHoverSlot({ x: r.left, y: r.bottom + 8, pick: `1.${String(slotNum).padStart(2, '0')}`, dist });
                  }}
                  onMouseLeave={() => setHoverSlot(null)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: '10px 6px', borderRadius: 12, cursor: 'pointer',
                    background: '#F5F5F7', textAlign: 'center',
                  }}
                >
                  <Avatar team={originalTeam} size={32} />
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#1D1D1F', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {originalTeam?.displayName || `Team ${slot.rosterId}`}
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: slot.madePlayoffs ? '#34C759' : '#C7C7CC' }}>
                    {slot.madePlayoffs ? 'Made playoffs' : 'Missed'}
                  </div>
                </div>

                {/* Cells — one per round */}
                {rounds.map(round => {
                  const pickKey = `${season}:${round}:${slot.rosterId}`;
                  const ownerId = ownerOfPick[pickKey];
                  const traded = ownerId != null && ownerId !== slot.rosterId;
                  const ownerTeam = ownerId != null ? teamById[ownerId] : originalTeam;
                  const mineOwned = (ownerId != null ? ownerId : slot.rosterId) === myRosterId;
                  const justAcquired = acquiredKeys.has(pickKey); // picked up in the active trade
                  const justSent = sentKeys.has(pickKey);         // sent away in the active trade
                  const pickLabel = `${round}.${String(slotNum).padStart(2, '0')}`;
                  return (
                    <div
                      key={round}
                      onClick={() => onSelectTeam && onSelectTeam(ownerId != null ? ownerId : slot.rosterId)}
                      style={{
                        position: 'relative', height: 64, borderRadius: 10, padding: '8px 8px 6px',
                        cursor: 'pointer', transition: 'transform 0.12s',
                        background: justAcquired ? 'rgba(52,199,89,0.12)' : justSent ? 'rgba(255,59,48,0.07)' : mineOwned ? 'rgba(255,149,0,0.10)' : '#fff',
                        border: justAcquired ? '1.5px solid rgba(52,199,89,0.55)' : justSent ? '1.5px solid rgba(255,59,48,0.4)' : mineOwned ? '1.5px solid rgba(255,149,0,0.5)' : '1px solid #ECECEE',
                        boxShadow: justAcquired ? '0 0 0 3px rgba(52,199,89,0.12)' : justSent ? '0 0 0 3px rgba(255,59,48,0.08)' : 'none',
                        opacity: justSent ? 0.92 : 1,
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1D1D1F', fontVariantNumeric: 'tabular-nums' }}>{pickLabel}</span>
                        {justAcquired
                          ? <span style={{ fontSize: 7.5, fontWeight: 800, background: '#34C759', color: '#fff', borderRadius: 3, padding: '1px 3px', letterSpacing: '0.5px' }}>NEW</span>
                          : justSent
                          ? <span style={{ fontSize: 7.5, fontWeight: 800, background: '#FF3B30', color: '#fff', borderRadius: 3, padding: '1px 3px', letterSpacing: '0.5px' }}>SENT</span>
                          : mineOwned && <span style={{ fontSize: 7.5, fontWeight: 800, background: '#FF9500', color: '#fff', borderRadius: 3, padding: '1px 3px', letterSpacing: '0.5px' }}>YOU</span>}
                      </div>
                      {justAcquired ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                          <span style={{ fontSize: 11, color: '#1a9640', fontWeight: 800, flexShrink: 0 }}>↗</span>
                          <span style={{ fontSize: 10, color: '#1a9640', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>acquired</span>
                        </div>
                      ) : justSent ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                          <span style={{ fontSize: 11, color: '#c0392b', fontWeight: 800, flexShrink: 0 }}>↘</span>
                          <Avatar team={ownerTeam} size={16} />
                          <span style={{ fontSize: 10, color: '#c0392b', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            to {ownerTeam?.displayName || `Team ${ownerId}`}
                          </span>
                        </div>
                      ) : traded ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                          <span style={{ fontSize: 11, color: '#5AC8FA', fontWeight: 800, flexShrink: 0 }}>→</span>
                          <Avatar team={ownerTeam} size={16} />
                          <span style={{ fontSize: 10, color: '#6E6E73', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ownerTeam?.displayName || `Team ${ownerId}`}
                          </span>
                        </div>
                      ) : (
                        <div style={{ fontSize: 10, color: '#C7C7CC', fontWeight: 500 }}>own pick</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ flexShrink: 0, fontSize: 11, color: '#C7C7CC', textAlign: 'center', padding: '0 24px 16px' }}>
        Scroll sideways for every slot · <span style={{ color: '#5AC8FA' }}>→</span> means the pick was traded to that team
      </div>

      {/* Slot probability tooltip — who's most likely to land in this draft slot */}
      {hoverSlot && hoverSlot.dist.length > 0 && (
        <div style={{
          position: 'fixed', top: hoverSlot.y, left: hoverSlot.x, zIndex: 200,
          background: '#1D1D1F', borderRadius: 12, padding: '11px 13px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.22)', minWidth: 200, maxWidth: 260,
          pointerEvents: 'none', fontFamily: ff,
        }}>
          <div style={{ position: 'absolute', top: -5, left: 22, width: 10, height: 10, background: '#1D1D1F', transform: 'rotate(45deg)', borderRadius: 2 }} />
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>Who lands at pick {hoverSlot.pick}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {hoverSlot.dist.map(({ team, p }) => (
              <div key={team.rosterId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar team={team} size={18} />
                <span style={{ fontSize: 12, color: '#fff', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{team.displayName}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#7ee2a8', fontVariantNumeric: 'tabular-nums' }}>{`${(p * 100).toFixed(p < 0.1 ? 1 : 0)}%`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
